import React, { useState, useEffect } from 'react';
import { Database, Cloud, CheckCircle, AlertCircle, RefreshCw, UploadCloud, HardDrive, ShieldCheck, ExternalLink, Zap, Mail, Clock, Phone, User, MessageSquare, Lock, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { 
  getAgentsFromFirebase, 
  getInquiriesFromFirebase, 
  saveAgentToFirebase,
  InquiryData
} from '../../services/firebaseService';
import { Apartment, Agent } from '../../types';

interface FirebaseManagerProps {
  properties: Apartment[];
  agents: Agent[];
  onRefreshProperties?: () => void;
}

export default function FirebaseManager({
  properties,
  agents,
  onRefreshProperties
}: FirebaseManagerProps) {
  const { user, signOut } = useAuth();
  const [isChecking, setIsChecking] = useState(false);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [cloudStats, setCloudStats] = useState<{
    propertiesCount: number;
    agentsCount: number;
    inquiriesCount: number;
    lastSynced: string;
  }>({
    propertiesCount: 0,
    agentsCount: 0,
    inquiriesCount: 0,
    lastSynced: 'Не е синхронизирано'
  });
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);
  const [recentInquiries, setRecentInquiries] = useState<InquiryData[]>([]);

  // Test Firebase and Properties API connection
  const checkConnection = async () => {
    setIsChecking(true);
    try {
      // 1. Fetch properties from Cloudflare KV / API
      let propCount = properties.length;
      try {
        const res = await fetch('/api/properties');
        if (res.ok) {
          const ct = res.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            const data = await res.json();
            if (Array.isArray(data)) {
              propCount = data.length;
            }
          }
        }
      } catch (kvErr: any) {
        if (kvErr?.name !== 'AbortError' && !kvErr?.message?.includes('aborted')) {
          console.warn('Properties API fetch warning:', kvErr);
        }
      }

      // 2. Fetch agents and inquiries from Firestore
      const fbAgents = await getAgentsFromFirebase();
      const fbInquiries = await getInquiriesFromFirebase();

      setIsConnected(true);
      setRecentInquiries(fbInquiries.slice(0, 10));
      setCloudStats({
        propertiesCount: propCount,
        agentsCount: fbAgents.length,
        inquiriesCount: fbInquiries.length,
        lastSynced: new Date().toLocaleTimeString('bg-BG')
      });
    } catch (err) {
      console.warn('Firebase test connection failed:', err);
      setIsConnected(false);
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    checkConnection();
  }, []);

  // Upload local agents to Firestore
  const handleBulkUploadAgentsToFirebase = async () => {
    setIsSyncingAll(true);
    setSyncStatus('Започва експорт на служителите към Firebase Firestore...');
    try {
      let agentSuccess = 0;
      for (const a of agents) {
        await saveAgentToFirebase(a);
        agentSuccess++;
      }

      setSyncStatus(`Успешно синхронизирани ${agentSuccess} служители във Firebase Firestore!`);
      await checkConnection();
    } catch (err) {
      console.error('Bulk sync error:', err);
      setSyncStatus('Възникна грешка при качване на екипа.');
    } finally {
      setIsSyncingAll(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner with Project Credentials Info */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-150">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-600 font-bold">
              <Database className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-base font-bold text-[#0F1E32] flex items-center gap-2">
                Облачна база данни & Резервни копия
                {isConnected ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                    <CheckCircle className="w-3.5 h-3.5" /> Свързана
                  </span>
                ) : isChecking ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">
                    <RefreshCw className="w-3 h-3 animate-spin" /> Проверка...
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800">
                    <AlertCircle className="w-3.5 h-3.5" /> Офлайн / Локален режим
                  </span>
                )}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Облачна среда: <code className="font-mono text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded">imotencentervarna-943c3</code>
              </p>
            </div>
          </div>

          <button
            onClick={checkConnection}
            disabled={isChecking}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-[#0F1E32] rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isChecking ? 'animate-spin' : ''}`} />
            Обнови Статуса
          </button>
        </div>

        {/* Live Counters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
              Оферти в Облака
            </span>
            <div className="text-2xl font-black text-[#0F1E32]">
              {cloudStats.propertiesCount}
            </div>
            <span className="text-[10px] text-slate-500">
              Локално в сайта: {properties.length}
            </span>
          </div>

          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
              Брокери и Екип
            </span>
            <div className="text-2xl font-black text-[#0F1E32]">
              {cloudStats.agentsCount}
            </div>
            <span className="text-[10px] text-slate-500">
              Локално в сайта: {agents.length}
            </span>
          </div>

          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
              Запитвания / Заявки
            </span>
            <div className="text-2xl font-black text-[#0F1E32]">
              {cloudStats.inquiriesCount}
            </div>
            <span className="text-[10px] text-slate-500">
              Последна проверка: {cloudStats.lastSynced}
            </span>
          </div>
        </div>
      </div>

      {/* Authentication Status Card */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-150">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-600 font-bold">
              <Lock className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-base font-bold text-[#0F1E32] flex items-center gap-2">
                Администраторски достъп
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                  <CheckCircle className="w-3.5 h-3.5" /> Активна сесия
                </span>
              </h4>
              <p className="text-xs text-slate-500 mt-0.5">
                Влезли сте като: <strong className="text-slate-800">{user?.email || 'Администратор'}</strong> (Защитен администраторски профил)
              </p>
            </div>
          </div>

          <button
            onClick={() => signOut()}
            className="px-4 py-2 bg-slate-100 hover:bg-red-50 hover:text-red-700 text-slate-700 rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition cursor-pointer"
          >
            <LogOut className="w-3.5 h-3.5" />
            Изход от профила
          </button>
        </div>
      </div>

      {/* Hosting & Media Workflow Info */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 text-slate-800">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#0F1E32] text-[#C5A46E] flex items-center justify-center shrink-0">
            <Zap className="w-5 h-5" />
          </div>
          <div className="space-y-2">
            <h4 className="font-bold text-sm text-[#0F1E32]">
              📁 Управление на снимки и видеа чрез папка <code>public/</code>
            </h4>
            <p className="text-xs text-slate-700 leading-relaxed">
              Снимките и видеоклиповете се добавят директно в папка <code>public/</code> на проекта и се рефрешва хостинга:
            </p>
            <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
              <li>Поставете файла си в <code>public/</code> (напр. <code>public/my-apartment.jpg</code> или <code>public/tour.mp4</code>).</li>
              <li>В админ панела за снимка на имота/служителя просто запишете пътя: <code>/my-apartment.jpg</code>.</li>
              <li>След обновяване на хостинга, файлът се зарежда мигновено и с нулеви допълнителни разходи.</li>
              <li><strong>Firebase Firestore:</strong> Съхранява цялата текстова информация, цени, описания, списък с брокери и запитвания от формите.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Recent Client Inquiries & Email Notifications */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-150">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <h4 className="text-sm font-bold text-[#0F1E32] uppercase tracking-wider flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#C5A46E]" />
              Клиентски запитвания по имейл & Firestore
            </h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Всички клиентски съобщения се изпращат автоматично на имейла на фирмата (<code className="font-mono font-bold text-slate-700">imotencentervarna1@gmail.com</code>) и се архивират в базата данни.
            </p>
          </div>
          <span className="px-3 py-1 bg-amber-50 border border-amber-200 text-amber-800 rounded-full text-xs font-bold shrink-0 self-start sm:self-auto">
            {cloudStats.inquiriesCount} получени
          </span>
        </div>

        {recentInquiries.length === 0 ? (
          <div className="p-6 bg-slate-50 rounded-xl border border-slate-100 text-center text-xs text-slate-500">
            Няма регистрирани нови запитвания в базата все още. При изпращане от формите в сайта те ще се покажат тук и ще пристигнат на Вашия имейл.
          </div>
        ) : (
          <div className="space-y-3">
            {recentInquiries.map((inq, idx) => (
              <div key={inq.id || idx} className="p-4 bg-slate-50 hover:bg-amber-50/40 rounded-xl border border-slate-150 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-[#0F1E32] flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-[#C5A46E]" /> {inq.name}
                    </span>
                    {inq.propertyTitle && (
                      <span className="text-[11px] bg-slate-200 text-slate-700 px-2 py-0.5 rounded-md font-medium truncate max-w-xs">
                        Оферта: {inq.propertyTitle}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1">
                    <Clock className="w-3 h-3 text-slate-400" />
                    {inq.createdAt ? new Date(inq.createdAt).toLocaleString('bg-BG') : 'Наскоро'}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600 mb-2">
                  <span className="font-semibold text-slate-700 flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5 text-emerald-600" /> {inq.phone}
                  </span>
                  {inq.email && (
                    <span className="flex items-center gap-1 text-slate-500">
                      <Mail className="w-3.5 h-3.5 text-blue-500" /> {inq.email}
                    </span>
                  )}
                </div>

                {inq.message && (
                  <p className="text-xs text-slate-700 bg-white p-3 rounded-lg border border-slate-100 font-sans leading-relaxed">
                    {inq.message}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Synchronization Actions */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-150">
        <h4 className="text-sm font-bold text-[#0F1E32] uppercase tracking-wider mb-2">
          Синхронизация на служителите
        </h4>
        <p className="text-xs text-slate-500 mb-5">
          Имотите се управляват и съхраняват директно чрез Cloudflare KV и Estate Assistant CRM. Служителите и контактните лица се съхраняват в облака на Firebase Firestore.
        </p>

        {syncStatus && (
          <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-900 text-xs font-semibold flex items-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin shrink-0" />
            <span>{syncStatus}</span>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleBulkUploadAgentsToFirebase}
            disabled={isSyncingAll}
            className="px-5 py-2.5 bg-[#0F1E32] hover:bg-[#C5A46E] text-white font-bold rounded-lg text-xs uppercase tracking-wider shadow transition flex items-center gap-2 disabled:opacity-50"
          >
            <UploadCloud className="w-4 h-4" />
            {isSyncingAll ? 'Качване в облака...' : 'Качи всички служители във Firestore'}
          </button>
        </div>
      </div>
    </div>
  );
}
